import {
  browserTracingIntegration,
  captureConsoleIntegration,
  init,
} from "@sentry/react";

export const createSentryTracing = () => {
  if (import.meta.env.PUBLIC_SENTRY === "true") {
    const ignoreList: string[] = [];

    init({
      dsn: import.meta.env.PUBLIC_SENTRY_DNS,
      // This enables automatic instrumentation (highly recommended),
      // but is not necessary for purely manual usage
      // If you only want to use custom instrumentation:
      // * Remove the `BrowserTracing` integration
      // * add `Sentry.addTracingExtensions()` above your Sentry.init() call
      integrations: [
        browserTracingIntegration(),
        captureConsoleIntegration({
          levels: ["error", "debug", "assert"],
        }),
      ],
      sendDefaultPii: true,
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control
      tracesSampleRate: 0,
      // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
      tracePropagationTargets: [window.location.host],
      maxValueLength: Number.MAX_SAFE_INTEGER,
      normalizeDepth: Number.MAX_SAFE_INTEGER,
      // Allow reports only from actual site and not third-party junk (ads, analytics, browser extensions, etc.).
      allowUrls: [window.location.host],
      ignoreErrors: ["NetworkError"],

      beforeSend(event) {
        for (const exception of event.exception?.values ?? []) {
          const message = exception.value?.toLowerCase();
          const isIgnored = ignoreList.some(
            (value) => message?.includes(value.toLowerCase()) === true,
          );

          if (isIgnored) {
            return null;
          }
        }

        return event;
      },
    });
  }
};

export const createNetworkErrorCause = (props: {
  message: string;
  input: RequestInfo | URL;
  init?: RequestInit;
  data?: unknown;
}) => {
  const requestData = getSerializedRequestInitBody(props.init);

  return [
    `${props.message}`,
    `api: ${props.input.toString()}`,
    `request-data: ${requestData?.toString()}`,
    `response-data: ${JSON.stringify(props.data)}`,
  ].join(`\n\t`);
};

const getSerializedRequestInitBody = ({ body }: RequestInit = {}) => {
  if (body instanceof FormData) {
    return `FormData ${JSON.stringify(Object.fromEntries(Array.from(body)))}`;
  }

  return body;
};
